import { createGrid, GridApi, ColDef, GridOptions, IRowNode } from 'ag-grid-community';
import type { CsvRow } from '../types';
import { isCurrencyField } from '../utils/field-utils';
import { formatGermanCurrency } from '../utils/german-number';
import { setGridApi, setColumnDefs } from '../state/app-state';
import { hasFilterConditions, evaluateNode, getFilterRoot } from '../filter/filter-logic';
import { initFilterBuilder } from '../filter/filter-renderer';

export function generateColumnDefs(data: CsvRow[]): ColDef[] {
  if (!data || data.length === 0) return [];

  const firstRow = data[0];
  return Object.keys(firstRow)
    .filter(key => key && key.trim() !== '')
    .map(col => {
      const colDef: ColDef = {
        field: col,
        headerName: col.replace(/\n/g, ' '),
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 120
      };

      if (isCurrencyField(col)) {
        colDef.type = 'numericColumn';
        colDef.filter = 'agNumberColumnFilter';
        colDef.valueFormatter = (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          if (typeof params.value === 'number') {
            return formatGermanCurrency(params.value);
          }
          return params.value;
        };
      }

      return colDef;
    });
}

function findGesamtsummeColumn(columnDefs: ColDef[]): string | null {
  const match = columnDefs.find(col =>
    col.field && col.field.toLowerCase().includes('gesamtsumme')
  );
  return match ? (match.field as string) : null;
}

function isExternalFilterPresent(): boolean {
  return hasFilterConditions();
}

function doesExternalFilterPass(node: IRowNode): boolean {
  if (!node.data) return true;
  return evaluateNode(node.data, getFilterRoot());
}

export function initGrid(
  container: HTMLElement,
  data: CsvRow[],
  onGridReady: () => void,
  onFilterChanged: () => void
): GridApi {
  const columnDefs = generateColumnDefs(data);
  setColumnDefs(columnDefs);

  const gesamtsummeCol = findGesamtsummeColumn(columnDefs);

  const initialState = gesamtsummeCol ? {
    sort: {
      sortModel: [{ colId: gesamtsummeCol, sort: 'desc' as const }]
    }
  } : {};

  const gridOptions: GridOptions = {
    columnDefs,
    rowData: data,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true
    },
    initialState,
    animateRows: true,
    isExternalFilterPresent,
    doesExternalFilterPass,
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
      onGridReady();
    },
    onFilterChanged
  };

  container.innerHTML = '';
  const gridApi = createGrid(container, gridOptions);
  setGridApi(gridApi);

  initFilterBuilder(columnDefs);

  return gridApi;
}
